import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    Menu,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React from 'react';
import dayjs from 'dayjs';
import { TemplateDisplay, TemplateRevision } from '../../apiClients/TemplateClient';
import IconButtonEx from '../../components/IconButtonEx';
import OnlyForRole from '../../components/OnlyForRole';
import { Securables, UpsertAction } from '../../Enums';
import { FormatDateTime } from '../../Helpers';
import TemplateCopyPopup from '../../popups/TemplateCopyPopup';
import TemplateEditorPopup from '../../popups/TemplateEditorPopup';
import useViewportMode from '../../hooks/useViewportMode';

export interface TemplateManagementDisplay extends TemplateDisplay {
    TemplateRevisions: TemplateRevision[];
}

interface ModernTemplateManagementViewProps {
    templates: TemplateManagementDisplay[];
    positionNames: Record<string, string>;
    canModifyTemplates: boolean;
    canCreateTemplates: boolean;
    onRename: (templateId: string, name: string) => Promise<void>;
    onDelete: (templateId: string) => Promise<void>;
    onReload: () => Promise<void>;
}

const ActionLabel = ({ icon, children }: { icon: JSX.Element; children: string }) =>
    <Stack direction="row" spacing={0.75} alignItems="center">
        {icon}
        <Typography variant="button" sx={{ whiteSpace: 'nowrap', lineHeight: 1 }}>{children}</Typography>
    </Stack>;

const formatDate = (value?: string) => value
    ? FormatDateTime(dayjs(value).toString())
    : 'Not available';

const revisionStatus = (revision: TemplateRevision) => {
    if (revision.IsDeleted)
        return { label: 'Deleted', color: 'default' as const, variant: 'outlined' as const };
    if (revision.IsActive)
        return { label: 'Published', color: 'success' as const, variant: 'filled' as const };
    return { label: 'Draft', color: 'info' as const, variant: 'outlined' as const };
};

const getPositionId = (template: TemplateManagementDisplay) =>
    template.TemplateRevisions.find(revision => !revision.IsDeleted)?.PositionId
    ?? template.TemplateRevisions[0]?.PositionId
    ?? template.PositionId;

const ModernTemplateManagementView = ({
    templates,
    positionNames,
    canModifyTemplates,
    canCreateTemplates,
    onRename,
    onDelete,
    onReload,
}: ModernTemplateManagementViewProps) => {
    const { mode, isDesktop } = useViewportMode();
    const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
    const [menuTemplateId, setMenuTemplateId] = React.useState<string | null>(null);
    const [editingTemplateId, setEditingTemplateId] = React.useState<string | null>(null);
    const [editingName, setEditingName] = React.useState('');
    const [savingName, setSavingName] = React.useState(false);
    const renameInputRef = React.useRef<HTMLInputElement | null>(null);

    React.useEffect(() => {
        if (editingTemplateId)
            renameInputRef.current?.select();
    }, [editingTemplateId]);

    const beginRename = (template: TemplateManagementDisplay) => {
        setEditingTemplateId(template.Id);
        setEditingName(template.Name);
    };

    const cancelRename = () => {
        setEditingTemplateId(null);
        setEditingName('');
    };

    const saveRename = async () => {
        const nextName = editingName.trim();
        if (!editingTemplateId || !nextName)
            return;
        setSavingName(true);
        try {
            await onRename(editingTemplateId, nextName);
            cancelRename();
        } catch {
            // The page-level error display owns the message. Preserve the
            // entered name so the user can correct or retry it.
        } finally {
            setSavingName(false);
        }
    };
    const actionButtonSx = {
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        px: 1,
        ...(!isDesktop && { minWidth: 44, minHeight: 44 }),
    };

    if (templates.length === 0) {
        return <Box sx={{ py: 10, textAlign: 'center' }}>
            <Typography variant="h6">No templates found</Typography>
            <Typography color="text.secondary">Try changing the search or create a new template.</Typography>
        </Box>;
    }

    return <Box
        data-testid="modern-template-grid"
        data-viewport-mode={mode}
        sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 380px), 1fr))',
            gap: 2,
            alignItems: 'start',
            p: isDesktop ? 2 : { xs: 1.5, sm: 2 },
        }}>
        {templates.map(template => {
            const revisions = [...(template.TemplateRevisions ?? [])]
                .sort((left, right) => right.Revision - left.Revision);
            const positionId = getPositionId(template);
            const positionName = positionNames[positionId] ?? 'Position unavailable';
            const hasPublishedRevision = revisions.some(revision => revision.IsActive);
            const canDelete = canModifyTemplates && !hasPublishedRevision && !template.IsDeleted;
            const hasTemplateActions = canCreateTemplates || canDelete;

            return <Paper
                component="article"
                aria-label={`${template.Name} template`}
                key={template.Id}
                variant="outlined"
                sx={{ minWidth: 0, overflow: 'hidden' }}>
                <Stack
                    spacing={1.5}
                    sx={{ p: 2, bgcolor: 'background.paper' }}>
                    <Box sx={{ minWidth: 0, position: 'relative', '&:hover .template-rename, &:focus-within .template-rename': { opacity: 1 } }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ pr: 5 }}>
                            {editingTemplateId === template.Id
                                ? <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flex: '1 1 240px' }}>
                                    <TextField
                                        inputRef={renameInputRef}
                                        size="small"
                                        value={editingName}
                                        label="Template name"
                                        onChange={event => setEditingName(event.target.value)}
                                        onKeyDown={event => {
                                            if (event.key === 'Enter') {
                                                event.preventDefault();
                                                void saveRename();
                                            } else if (event.key === 'Escape') {
                                                event.preventDefault();
                                                cancelRename();
                                            }
                                        }}
                                    />
                                    <Button size="small" variant="contained" disabled={savingName || !editingName.trim()} onClick={() => void saveRename()}>Save</Button>
                                </Stack>
                                : <Stack direction="row" spacing={0.25} alignItems="center" sx={{ minWidth: 0 }}>
                                    <Typography variant="h5" sx={{ overflowWrap: 'anywhere' }}>{template.Name}</Typography>
                                    {canModifyTemplates && <Tooltip title="Rename template">
                                        <IconButton
                                            className="template-rename"
                                            size="small"
                                            aria-label={`Rename ${template.Name}`}
                                            onClick={() => beginRename(template)}
                                            sx={{ opacity: { xs: 1, md: 0 }, transition: 'opacity 120ms ease' }}>
                                            <EditOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>}
                                </Stack>}
                            <Chip
                                size="small"
                                color={hasPublishedRevision ? 'success' : 'default'}
                                variant={hasPublishedRevision ? 'filled' : 'outlined'}
                                label={template.ActiveStatus || (hasPublishedRevision ? 'Published' : 'Not published')}
                            />
                        </Stack>
                        {hasTemplateActions && <Tooltip title="Template actions">
                            <IconButton
                                size="small"
                                aria-label={`Actions for ${template.Name}`}
                                onClick={event => {
                                    setMenuAnchor(event.currentTarget);
                                    setMenuTemplateId(template.Id);
                                }}
                                sx={{ position: 'absolute', right: 0, top: 0 }}>
                                <MoreVertIcon />
                            </IconButton>
                        </Tooltip>}
                        <Typography variant="body1" sx={{ mt: 0.5 }}><strong>Position:</strong> {positionName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Last modified {formatDate(template.LastUpdateDate)} · {revisions.length} {revisions.length === 1 ? 'revision' : 'revisions'}
                        </Typography>
                    </Box>

                    {hasTemplateActions && <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor) && menuTemplateId === template.Id}
                        onClose={() => setMenuAnchor(null)}>
                        {canCreateTemplates && <OnlyForRole role={Securables.template_create}>
                                <TemplateCopyPopup
                                    templateId={template.Id}
                                    templateName={template.Name}
                                    onAdd={onReload}
                                    aria-label={`Copy ${template.Name}`}
                                    onMenuAction={() => setMenuAnchor(null)}
                                    menuItemLabel={<ActionLabel icon={<ContentCopyOutlinedIcon fontSize="small" />}>Copy</ActionLabel>}
                                />
                        </OnlyForRole>}
                        {canDelete && <OnlyForRole role={Securables.template_modify}>
                                <IconButtonEx
                                    aria-label={`Delete ${template.Name}`}
                                    hoverContent="Delete template"
                                    headerContent="Delete template?"
                                    message={`Are you sure you want to delete ${template.Name}?`}
                                    onClick={() => onDelete(template.Id)}
                                    onMenuAction={() => setMenuAnchor(null)}
                                    menuItemLabel={<ActionLabel icon={<DeleteOutlineIcon color="error" fontSize="small" />}>Delete</ActionLabel>}
                                />
                        </OnlyForRole>}
                    </Menu>}
                </Stack>

                <Divider />
                <Box
                    role="list"
                    aria-label={`${template.Name} revision history`}
                    sx={isDesktop
                        ? { maxHeight: 260, overflowY: 'auto', scrollbarGutter: 'stable' }
                        : { maxHeight: 'none', overflowY: 'visible' }}>
                    {revisions.length === 0
                        ? <Typography color="text.secondary" sx={{ p: 2 }}>No revisions have been created.</Typography>
                        : revisions.map((revision, index) => {
                            const status = revisionStatus(revision);
                            const editorMode = revision.IsActive || revision.IsDeleted
                                ? UpsertAction.View
                                : canModifyTemplates ? UpsertAction.Update : UpsertAction.View;
                            const actionLabel = revision.IsActive
                                ? 'View published'
                                : revision.IsDeleted ? 'View revision' : canModifyTemplates ? 'Edit revision' : 'View revision';

                            return <Box
                                role="listitem"
                                key={revision.Id}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
                                    columnGap: 1,
                                    rowGap: 0.75,
                                    alignItems: 'center',
                                    px: 2,
                                    py: 1.5,
                                    borderTop: index === 0 ? 0 : 1,
                                    borderColor: 'divider',
                                    bgcolor: revision.IsActive ? 'action.hover' : 'transparent',
                                }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography fontWeight={700}>Revision {revision.Revision}</Typography>
                                    <Chip size="small" {...status} />
                                </Stack>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ gridColumn: '1 / -1' }}>
                                    Modified {formatDate(revision.LastUpdateDate)}
                                </Typography>
                                <OnlyForRole role={Securables.template}>
                                    <TemplateEditorPopup
                                        mode={editorMode}
                                        templateId={revision.TemplateId}
                                        templateRevisionId={revision.Id}
                                        onCreateRevision={onReload}
                                        onPublish={onReload}
                                        aria-label={`${actionLabel} ${revision.Revision} of ${template.Name}`}
                                        hoverContent={`${actionLabel} ${revision.Revision}`}
                                        sx={{
                                            ...actionButtonSx,
                                            gridColumn: { xs: '1', sm: '2' },
                                            gridRow: { sm: '1' },
                                            justifySelf: { xs: 'start', sm: 'end' },
                                        }}
                                        icon={<ActionLabel
                                            icon={editorMode === UpsertAction.Update
                                                ? <EditOutlinedIcon fontSize="small" />
                                                : <VisibilityOutlinedIcon fontSize="small" />}>
                                            {actionLabel}
                                        </ActionLabel>}
                                    />
                                </OnlyForRole>
                            </Box>;
                        })}
                </Box>
            </Paper>;
        })}
    </Box>;
};

export default ModernTemplateManagementView;
